// src/components/sliders/DeleteSliderConfirmModal.js
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { FaTrash } from "react-icons/fa";
import { doc, deleteDoc } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";

const DeleteSliderConfirmModal = ({ slider, show, onHide, onSuccess }) => {
  const [deleting, setDeleting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  if (!slider) return null;

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setErrorMsg("");

      // 1. Delete Firestore slider document
      await deleteDoc(doc(db, "sliders", slider.id));

      // 2. Delete corresponding Firebase Storage object using storagePath
      if (slider.storagePath) {
        try {
          const fileRef = ref(storage, slider.storagePath);
          await deleteObject(fileRef);
        } catch (storageErr) {
          console.warn(
            "Storage deletion notice (file may have already been removed or does not exist):",
            storageErr
          );
        }
      }

      if (onSuccess) {
        onSuccess("Slider deleted successfully!");
      }
      onHide();
    } catch (err) {
      console.error("Failed to delete slider document:", err);
      setErrorMsg(err.message || "Failed to delete slider from database. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => !deleting && onHide()}
      centered
      backdrop={deleting ? "static" : true}
      size="sm"
    >
      <div
        style={{
          background: "var(--bg-surface)",
          border: "1px solid rgba(239, 68, 68, 0.25)",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "18px 20px",
            borderBottom: "1px solid var(--divider)",
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "rgba(239, 68, 68, 0.15)",
              color: "#EF4444",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaTrash size={16} />
          </div>
          <div>
            <h6 style={{ margin: 0, fontWeight: 700, color: "#EF4444", fontSize: 14 }}>
              Delete Slider
            </h6>
            <small style={{ color: "var(--text-secondary)", fontSize: 11 }}>
              This action cannot be undone
            </small>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "20px" }}>
          {errorMsg && (
            <div className="alert alert-danger py-2 px-3 rounded-3 small mb-3" role="alert">
              {errorMsg}
            </div>
          )}

          <p style={{ color: "var(--text-primary)", fontSize: 13, marginBottom: 14 }}>
            Are you sure you want to permanently delete this slider banner and remove its image from storage?
          </p>

          {/* Slider summary preview */}
          <div
            style={{
              background: "rgba(239, 68, 68, 0.06)",
              border: "1px solid rgba(239, 68, 68, 0.2)",
              borderRadius: 8,
              padding: "12px",
            }}
          >
            {slider.imageUrl && (
              <div
                style={{
                  width: "100%",
                  height: 90,
                  borderRadius: 6,
                  overflow: "hidden",
                  marginBottom: 10,
                  background: "#000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={slider.imageUrl}
                  alt="Banner preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: 90,
                    objectFit: "contain",
                  }}
                />
              </div>
            )}
            <div className="d-flex align-items-center justify-content-between">
              <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                Sort Order: <strong style={{ color: "var(--text-primary)" }}>#{slider.sortOrder}</strong>
              </span>
              <span
                className={`badge ${slider.isActive ? "bg-success" : "bg-danger"}`}
                style={{ fontSize: 10, textTransform: "uppercase" }}
              >
                {slider.isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            padding: "14px 20px",
            borderTop: "1px solid var(--divider)",
          }}
        >
          <button
            type="button"
            onClick={onHide}
            disabled={deleting}
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 8,
              color: "var(--text-secondary)",
              fontSize: 13,
              fontWeight: 600,
              padding: "7px 16px",
              cursor: deleting ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            style={{
              background: "#EF4444",
              border: "none",
              borderRadius: 8,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              padding: "7px 20px",
              cursor: deleting ? "not-allowed" : "pointer",
              minWidth: 90,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            {deleting ? (
              <span className="admin-loader-sm" style={{ borderTopColor: "#fff" }} />
            ) : (
              "Delete"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteSliderConfirmModal;
